import {
  collection,
  count,
  getAggregateFromServer,
  getCountFromServer,
  query,
  sum,
  where,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { COLLECTIONS } from "@/firestore/collections";
import type { CollectionName } from "@/firestore/collections";

export interface PlatformSnapshot {
  users: number;
  wallets: number;
  pendingWallets: number;
  balanceSnapshots: number;
  earnPositions: number;
  main: bigint;
  reserve: bigint;
  spendable: bigint;
  held: bigint;
  earn: bigint;
  pendingEarn: bigint;
  fetchedAt: number;
}

export function aggregateInteger(value: number): bigint {
  if (!Number.isSafeInteger(value))
    throw new Error(
      "A platform aggregate is outside the supported integer range. Totals are unavailable.",
    );
  return BigInt(value);
}

async function totalCount(name: CollectionName, pendingOnly = false): Promise<number> {
  const ref = collection(db, name);
  const value = (
    await getCountFromServer(pendingOnly ? query(ref, where("state", "==", "pendingAddress")) : ref)
  ).data().count;
  aggregateInteger(value);
  return value;
}

async function totalField(name: CollectionName, field: string) {
  // Separate sums avoid the intersection-of-fields semantics of multi-field aggregations,
  // especially the optional pending-redemption field on legacy Earn positions.
  // A numeric range excludes null/string/missing/negative balances. Required-field
  // counts below must still cover every projection, rather than sum silently ignoring it.
  const result = await getAggregateFromServer(query(collection(db, name), where(field, ">=", 0)), {
    value: sum(field),
    rows: count(),
  });
  const { value, rows } = result.data();
  return { value: aggregateInteger(value), rows };
}

export async function fetchPlatformSnapshot(): Promise<PlatformSnapshot> {
  const [
    users,
    wallets,
    pendingWallets,
    balanceSnapshots,
    earnPositions,
    main,
    reserve,
    spendable,
    held,
    earn,
    pendingEarn,
  ] = await Promise.all([
    totalCount(COLLECTIONS.users),
    totalCount(COLLECTIONS.wallets),
    totalCount(COLLECTIONS.wallets, true),
    totalCount(COLLECTIONS.walletAvailability),
    totalCount(COLLECTIONS.earnPositions),
    totalField(COLLECTIONS.walletAvailability, "mainBalanceMicroUsdc"),
    totalField(COLLECTIONS.walletAvailability, "reserveBalanceMicroUsdc"),
    totalField(COLLECTIONS.walletAvailability, "spendableMicroUsdc"),
    totalField(COLLECTIONS.walletAvailability, "heldOnMainMicroUsdc"),
    totalField(COLLECTIONS.earnPositions, "tokenBalanceMicroUsdc"),
    totalField(COLLECTIONS.earnPositions, "pendingToMainValueMicroUsdc"),
  ]);
  if (
    [main, reserve, spendable, held].some((s) => s.rows !== balanceSnapshots) ||
    earn.rows !== earnPositions
  ) {
    throw new Error(
      "Balance coverage changed or required fields are missing. Refresh to obtain a complete snapshot.",
    );
  }
  if (wallets > pendingWallets && balanceSnapshots === 0)
    throw new Error("Wallet balance projections are not available yet.");
  return {
    users,
    wallets,
    pendingWallets,
    balanceSnapshots,
    earnPositions,
    main: main.value,
    reserve: reserve.value,
    spendable: spendable.value,
    held: held.value,
    earn: earn.value,
    pendingEarn: pendingEarn.value,
    fetchedAt: Date.now(),
  };
}
